/**
 * Product Analytics Service
 *
 * Abstraction layer for product analytics (currently PostHog).
 * This allows switching analytics providers without changing application code.
 *
 * Usage:
 * ```typescript
 * import { productAnalytics } from '@hapana/shared/web/services/productAnalytics'
 *
 * // Identify a user
 * productAnalytics.identify('user-123', { email_domain: 'hapana.com' })
 *
 * // Group user with a corporate
 * productAnalytics.group('corporate', 'corp-123', { name: 'Acme Corp' })
 *
 * // Track an event
 * productAnalytics.capture('button_clicked', { button_name: 'submit' })
 *
 * // Register super properties (sent with every event)
 * productAnalytics.register({ corporate_id: 'corp-123' })
 * ```
 */

import posthog from 'posthog-js'

// =============================================================================
// Types
// =============================================================================

/**
 * Product Analytics Client Interface
 *
 * Implement this interface to add a new analytics provider.
 */
export interface ProductAnalyticsClient {
  /**
   * Check if analytics is enabled and ready
   */
  isEnabled(): boolean

  /**
   * Identify a user
   * @param userId Unique user identifier
   * @param properties User properties (non-PII recommended)
   */
  identify(userId: string, properties?: Record<string, unknown>): void

  /**
   * Associate user with a group (e.g., corporate, site)
   * @param groupType Type of group (e.g., 'corporate', 'site')
   * @param groupId Unique group identifier
   * @param properties Group properties
   */
  group(groupType: string, groupId: string, properties?: Record<string, unknown>): void

  /**
   * Track a custom event
   * @param eventName Name of the event
   * @param properties Event properties
   */
  capture(eventName: string, properties?: Record<string, unknown>): void

  /**
   * Register super properties that will be sent with every event
   * @param properties Properties to register
   */
  register(properties: Record<string, unknown>): void

  /**
   * Unregister a super property
   * @param propertyName Name of the property to unregister
   */
  unregister(propertyName: string): void

  /**
   * Reset identity and super properties (call on logout)
   */
  reset(): void

  /**
   * Get feature flag value
   * @param flagKey Feature flag key
   * @returns Boolean or string value of the flag
   */
  getFeatureFlag(flagKey: string): boolean | string | undefined

  /**
   * Check if a feature is enabled
   * @param flagKey Feature flag key
   * @returns Boolean indicating if feature is enabled
   */
  isFeatureEnabled(flagKey: string): boolean

  /**
   * Get session replay URL (if available)
   * @returns URL to the current session replay
   */
  getSessionReplayUrl(): string | undefined

  /**
   * Opt user in to analytics
   */
  optIn(): void

  /**
   * Opt user out of analytics
   */
  optOut(): void
}

// =============================================================================
// PostHog Implementation
// =============================================================================

/**
 * PostHog implementation of ProductAnalyticsClient
 */
class PostHogAnalyticsClient implements ProductAnalyticsClient {
  isEnabled(): boolean {
    return Boolean(posthog && typeof posthog.capture === 'function')
  }

  identify(userId: string, properties?: Record<string, unknown>): void {
    if (!this.isEnabled()) return
    posthog.identify(userId, properties)
  }

  group(groupType: string, groupId: string, properties?: Record<string, unknown>): void {
    if (!this.isEnabled()) return
    posthog.group(groupType, groupId, properties)
  }

  capture(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.isEnabled()) return
    posthog.capture(eventName, properties)
  }

  register(properties: Record<string, unknown>): void {
    if (!this.isEnabled()) return
    posthog.register(properties)
  }

  unregister(propertyName: string): void {
    if (!this.isEnabled()) return
    posthog.unregister(propertyName)
  }

  reset(): void {
    if (!this.isEnabled()) return
    posthog.reset()
  }

  getFeatureFlag(flagKey: string): boolean | string | undefined {
    if (!this.isEnabled()) return undefined
    return posthog.getFeatureFlag(flagKey)
  }

  isFeatureEnabled(flagKey: string): boolean {
    if (!this.isEnabled()) return false
    return posthog.isFeatureEnabled(flagKey) ?? false
  }

  getSessionReplayUrl(): string | undefined {
    if (!this.isEnabled()) return undefined
    return posthog.get_session_replay_url?.() ?? undefined
  }

  optIn(): void {
    if (!this.isEnabled()) return
    posthog.opt_in_capturing()
  }

  optOut(): void {
    if (!this.isEnabled()) return
    posthog.opt_out_capturing()
  }
}

// =============================================================================
// Exports
// =============================================================================

/**
 * Default product analytics client (PostHog)
 */
export const productAnalytics: ProductAnalyticsClient = new PostHogAnalyticsClient()

/**
 * Create a custom analytics client (for testing or alternative providers)
 */
export function createProductAnalyticsClient(): ProductAnalyticsClient {
  return new PostHogAnalyticsClient()
}

export default productAnalytics
